import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Plus, Camera, FileUp, X, Image, FileText, Loader2 } from 'lucide-react';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { supabase } from '@/integrations/supabase/client';
import { ObraSelectorField } from './ObraSelectorField';
import { ClienteSelector } from './ClienteSelector';
import { toast } from '@/hooks/use-toast';

interface OrdenTrabajoFormProps {
  onSuccess?: () => void;
}

interface PendingFile {
  file: File;
  tipoDocumento: string;
  previewUrl?: string;
}

const DURACIONES = [
  { value: '', label: 'Sin especificar' },
  { value: '00:30:00', label: '30 minutos' },
  { value: '01:00:00', label: '1 hora' },
  { value: '01:30:00', label: '1.5 horas' },
  { value: '02:00:00', label: '2 horas' },
  { value: '03:00:00', label: '3 horas' },
  { value: '04:00:00', label: '4 horas' },
  { value: '08:00:00', label: 'Día completo' },
];

const TIPOS_TRABAJO = [
  { value: 'mantenimiento', label: 'Mantenimiento' },
  { value: 'revision', label: 'Revisión' },
  { value: 'instalacion', label: 'Instalación' },
  { value: 'puesta_en_marcha', label: 'Puesta en Marcha' },
  { value: 'auditoria', label: 'Auditoría' },
  { value: 'reparacion', label: 'Reparación/Avería' },
];

const TIPOS_DOCUMENTO = [
  { value: 'foto_sitio', label: 'Foto del sitio' },
  { value: 'presupuesto', label: 'Presupuesto' },
  { value: 'plano', label: 'Plano' },
  { value: 'especificacion', label: 'Especificación técnica' },
  { value: 'documentacion_cliente', label: 'Documentación cliente' },
  { value: 'otro', label: 'Otro' },
];

const initialFormData = {
  cliente_id: '',
  descripcion: '',
  prioridad: 'media' as const,
  tipo_trabajo: 'reparacion',
  obra_id: 'none',
  materiales_necesarios: '',
  duracion_estimada: '',
};

export const OrdenTrabajoForm: React.FC<OrdenTrabajoFormProps> = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [selectedTipoDoc, setSelectedTipoDoc] = useState('foto_sitio');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const { createOrden } = useOrdenesTrabajoContext();

  const resetForm = () => {
    setFormData(initialFormData);
    setPendingFiles([]);
    setSelectedTipoDoc('foto_sitio');
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleAddFile = (files: FileList | null, tipo: string) => {
    if (!files) return;
    
    const newFiles: PendingFile[] = [];
    Array.from(files).forEach(file => {
      const isImage = file.type.startsWith('image/');
      newFiles.push({
        file,
        tipoDocumento: tipo,
        previewUrl: isImage ? URL.createObjectURL(file) : undefined,
      });
    });
    
    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setPendingFiles(prev => {
      const file = prev[index];
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadFiles = async (ordenId: string) => {
    const { compressImageForUpload } = await import('@/lib/imageUtils');
    for (const pendingFile of pendingFiles) {
      try {
        const uploadFile = await compressImageForUpload(pendingFile.file);
        const fileExt = uploadFile.name.split('.').pop();
        const fileName = `${ordenId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('orden-trabajo-documentos')
          .upload(fileName, uploadFile, { contentType: uploadFile.type });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('orden-trabajo-documentos')
          .getPublicUrl(fileName);

        await supabase.from('orden_trabajo_documentos').insert({
          orden_trabajo_id: ordenId,
          nombre_archivo: pendingFile.file.name,
          tipo_documento: pendingFile.tipoDocumento,
          url_archivo: publicUrl,
          tamaño_archivo: uploadFile.size,
        });
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.cliente_id || !formData.descripcion) {
      toast({
        title: "Error",
        description: "Cliente y descripción son obligatorios",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const newOrden = await createOrden({
        titulo: formData.descripcion.substring(0, 100),
        descripcion: formData.descripcion,
        cliente_id: formData.cliente_id,
        prioridad: formData.prioridad,
        tipo_trabajo: formData.tipo_trabajo,
        estado: 'creada',
        obra_id: formData.obra_id && formData.obra_id !== 'none' ? formData.obra_id : null,
        materiales_necesarios: formData.materiales_necesarios || undefined,
        duracion_estimada: formData.duracion_estimada || undefined,
        creada_por: '',
      } as any);
      
      if (newOrden?.id && pendingFiles.length > 0) {
        await uploadFiles(newOrden.id);
      }

      toast({
        title: "OT creada",
        description: `Orden de trabajo creada correctamente${pendingFiles.length > 0 ? ` con ${pendingFiles.length} documento(s)` : ''}`,
      });
      
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error creating orden:', error);
      toast({
        title: "Error",
        description: "Error al crear la orden de trabajo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'baja': return 'text-blue-600';
      case 'media': return 'text-yellow-600';
      case 'alta': return 'text-orange-600';
      case 'urgente': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleClose();
      else setOpen(true);
    }}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Nueva Orden de Trabajo
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crear Nueva Orden de Trabajo</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Cliente con búsqueda */}
          <div className="space-y-2">
            <Label>Cliente *</Label>
            <ClienteSelector
              value={formData.cliente_id}
              onChange={(value) => setFormData(prev => ({ ...prev, cliente_id: value, obra_id: 'none' }))}
            />
          </div>

          {/* Prioridad y Tipo de Trabajo */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select 
                value={formData.prioridad} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, prioridad: value as any }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">
                    <span className={getPrioridadColor('baja')}>Baja</span>
                  </SelectItem>
                  <SelectItem value="media">
                    <span className={getPrioridadColor('media')}>Media</span>
                  </SelectItem>
                  <SelectItem value="alta">
                    <span className={getPrioridadColor('alta')}>Alta</span>
                  </SelectItem>
                  <SelectItem value="urgente">
                    <span className={getPrioridadColor('urgente')}>Urgente</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Trabajo</Label>
              <Select 
                value={formData.tipo_trabajo} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_trabajo: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_TRABAJO.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label>Descripción del trabajo *</Label>
            <Textarea
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Describir el trabajo a realizar..."
              rows={3}
              required
            />
          </div>

          {/* Duración estimada */}
          <div className="space-y-2">
            <Label>Duración Estimada</Label>
            <Select 
              value={formData.duracion_estimada} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, duracion_estimada: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin especificar" />
              </SelectTrigger>
              <SelectContent>
                {DURACIONES.map((dur) => (
                  <SelectItem key={dur.value || 'none'} value={dur.value || 'none'}>
                    {dur.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vincular a Obra (opcional) */}
          <ObraSelectorField
            clienteId={formData.cliente_id}
            value={formData.obra_id}
            onChange={(value) => setFormData(prev => ({ ...prev, obra_id: value }))}
          />

          {/* Materiales necesarios */}
          <div className="space-y-2">
            <Label>Materiales Necesarios (opcional)</Label>
            <Textarea
              value={formData.materiales_necesarios}
              onChange={(e) => setFormData(prev => ({ ...prev, materiales_necesarios: e.target.value }))}
              placeholder="Lista de materiales o herramientas necesarias..."
              rows={2}
            />
          </div>

          <Separator />

          {/* Documentos y Fotos */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Documentos y Fotos</Label>
            
            <div className="flex items-center gap-3">
              <Select value={selectedTipoDoc} onValueChange={setSelectedTipoDoc}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                Foto
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="h-4 w-4 mr-2" />
                Archivo
              </Button>
              
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleAddFile(e.target.files, selectedTipoDoc)}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
                multiple
                className="hidden"
                onChange={(e) => handleAddFile(e.target.files, selectedTipoDoc)}
              />
            </div>

            {/* Lista de archivos pendientes */}
            {pendingFiles.length > 0 && (
              <Card>
                <CardContent className="p-3 space-y-2">
                  {pendingFiles.map((pf, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-muted/50 rounded">
                      {pf.previewUrl ? (
                        <img src={pf.previewUrl} alt="" className="h-10 w-10 object-cover rounded" />
                      ) : (
                        <div className="h-10 w-10 flex items-center justify-center bg-muted rounded">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{pf.file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {TIPOS_DOCUMENTO.find(t => t.value === pf.tipoDocumento)?.label} • {formatFileSize(pf.file.size)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando...
                </>
              ) : (
                'Crear OT'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
